import { memo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';

import { excludeKey, setErrors } from '@utils/object';
import { Button, useSnackbar } from '@new-components/elements';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import {
  FormParam,
  useRGetApplicationData,
  useRSaveApplication,
  useRGetSavedApplication,
  SavedApplicationDataResponse,
} from '@repositories/new-application';

import ApplicationCard from '../../application-card';

import { defaultValues, personalIdentitySchema } from './constants';
import { PersonalIdentityProps } from './types';

import {
  SectionIdentityPhoto,
  SectionDomicile,
  SectionPersonalContact,
  SectionPersonalData,
} from './components/sections';
import { useActionLog } from '@hooks/use-action-log';
import BottomSheetConfirmation from './components/bottom-sheet-confirmation';
import MotherMaidenNameError from 'pages/UpdateUserInformation/components/MotherMaidenNameError';

const PersonalIdentityForm = (props: PersonalIdentityProps) => {
  const { setStep } = props;
  const { onOpen } = useSnackbar();
  const { pushEvent } = useActionLog();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<FormParam<'personal_identity'> | null>(null);
  const [showMotherMaidenNameError, setShowMotherMaidenNameError] =
    useState(false);

  const form = useForm<FormParam<'personal_identity'>>({
    defaultValues,
    resolver: yupResolver(personalIdentitySchema),
    shouldFocusError: false,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setError,
    reset,
    fieldsRef,
    formState: { errors },
  } = form;

  // Utility function to scroll to the first error field
  const scrollToFirstError = () => {
    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField) {
      const elementToScroll = fieldsRef.current[firstErrorField];

      if (elementToScroll && elementToScroll.scrollIntoView) {
        elementToScroll.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        // Focus the element after scrolling
        setTimeout(() => {
          if (elementToScroll.focus) {
            elementToScroll.focus();
          }
        }, 300);
      }
    }
  };

  const { isLoading: isLoadingGetApplicationData } = useRGetApplicationData({
    page: 1,
  });

  const { mutateAsync: mutateAsyncSaveForm, isLoading: isSavingForm } =
    useRSaveApplication({
      onError: (err) => {
        const statusCode = err.payload?.statusCode;
        const errors = (err.payload?.errors ||
          {}) as FormParam<'personal_identity'>;

        if (errors?.email) {
          pushEvent({
            element: 'email_exist',
            module: 'app_form_personal_data_page',
          });
        }

        switch (statusCode) {
          case 400:
            setErrors<FormParam<'personal_identity'>>(errors, setError);

            if (errors?.motherMaidenName) {
              setShowMotherMaidenNameError(true);
            }
            break;

          default:
            onOpen({
              message:
                (err.payload?.errors as Array<string>)?.[0] ||
                'Opps, Something went wrong.',
              severity: 'error',
            });
            break;
        }
      },
    });

  const isPhotosUpload =
    !!Boolean(watch('ktp')) && !!Boolean(watch('selfieKtp'));

  async function onSubmit(fields: FormParam<'personal_identity'>) {
    setPendingFormData(fields);
    setShowConfirmation(true);
  }

  async function handleConfirmSubmit() {
    if (!pendingFormData) return;

    try {
      await mutateAsyncSaveForm(
        {
          variables: {
            ...excludeKey<SavedApplicationDataResponse<'personal_identity'>>(
              pendingFormData as SavedApplicationDataResponse<'personal_identity'>,
              ['ktp', 'selfieKtp', 'currentStep'],
            ),
            step: 1,
          },
        },
        {
          onSuccess: () => {
            setStep((prev) => prev + 1);
            setShowConfirmation(false);
            setPendingFormData(null);
          },
        },
      );
    } catch (error) {
      setShowConfirmation(false);
      setPendingFormData(null);
      return;
    }
  }

  function handleCloseConfirmation() {
    setShowConfirmation(false);
    setPendingFormData(null);
  }

  useRGetSavedApplication<SavedApplicationDataResponse<'personal_identity'>>({
    step: 1,
    onSuccess: ({ data }) => {
      reset(data);
    },
  });

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <Helmet>
        <title>JULO - Form Data Diri </title>
      </Helmet>
      <form onSubmit={handleSubmit(onSubmit)} className='personal-identity'>
        <SectionIdentityPhoto />

        {isPhotosUpload && (
          <>
            <SectionPersonalData />
            <SectionDomicile />
            <SectionPersonalContact />

            <ApplicationCard>
              <Button
                type='submit'
                fullWidth
                disabled={isSavingForm || isLoadingGetApplicationData}
              >
                Lanjutkan
              </Button>
            </ApplicationCard>
          </>
        )}
      </form>

      <BottomSheetConfirmation
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmSubmit}
      />

      <MotherMaidenNameError
        open={showMotherMaidenNameError}
        onClose={() => {
          setShowMotherMaidenNameError(false);
          scrollToFirstError();
        }}
      />
    </FormProvider>
  );
};

export default memo(PersonalIdentityForm);
